import { createWorld, decodeGrid, isPaintable, wakeAll, Physique } from '@invaders/sim';
import type { Command } from './commands';
import { createGameState, plainMap, type GameState, type MapState } from './state';
import { generateIsland } from './terrain';
import { generateMap, isMapId, mapDef, type MapId } from './maps';
import { hashState, hashToHex, tick } from './tick';
import { createMatchState, type MatchSetup } from './match';

export interface ScenarioDesc {
  /**
   * A map of the GDD (MapId, built from `seed`), the plain island of step 1 ('island'), or 'custom': the map's
   * material ids travel in `map` (encodeGrid).
   */
  terrain: 'island' | 'custom' | MapId;
  seed: number;
  width: number;
  height: number;
  map?: string;
  /** 'sandbox' (default): every command allowed. 'match': turn-based rules, units placed by commands. */
  mode?: 'sandbox' | 'match';
  match?: MatchSetup;
}

export interface Checkpoint { tick: number; hash: string }

export interface ReplayLog {
  version: 1;
  scenario: ScenarioDesc;
  ticks: number;
  commands: Command[];
  expectedHash?: string;
  /** Hash every 60 ticks, to locate the first divergent tick. */
  expectedCheckpoints?: Checkpoint[];
}

export function createScenario(desc: ScenarioDesc): GameState {
  const world = createWorld(desc.width, desc.height, { seed: desc.seed, airless: isMapId(desc.terrain) && mapDef(desc.terrain).airless });
  let map: MapState = plainMap(desc.terrain);
  if (isMapId(desc.terrain)) map = generateMap(world, desc.terrain, desc.seed);
  else if (desc.terrain === 'island') generateIsland(world, desc.seed);
  else if (desc.terrain === 'custom') {
    const cells = decodeGrid(desc.map ?? '', desc.width * desc.height);
    for (let i = 0; i < cells.length; i++) if (!isPaintable(world, cells[i])) cells[i] = Physique.EMPTY;
    world.physique.data.set(cells);
    wakeAll(world);
  } else throw new Error(`unknown terrain ${String(desc.terrain)}`);
  const match = desc.mode === 'match' ? createMatchState(desc.match) : null;
  return createGameState(world, match, map);
}

/** Replays a command log from its scenario; returns the final hash and a checkpoint every `every` ticks. */
export function runReplay(log: ReplayLog, every = 60): { state: GameState; hash: string; checkpoints: Checkpoint[] } {
  const state = createScenario(log.scenario);
  const commands = [...log.commands].sort((a, b) => a.tick - b.tick);
  const checkpoints: Checkpoint[] = [];
  const batch: Command[] = [];
  let next = 0;
  while (state.tick < log.ticks) {
    batch.length = 0;
    while (next < commands.length && commands[next].tick <= state.tick) {
      if (commands[next].tick === state.tick) batch.push(commands[next]);
      next++;
    }
    tick(state, batch);
    if (state.tick % every === 0) checkpoints.push({ tick: state.tick, hash: hashToHex(hashState(state)) });
  }
  return { state, hash: hashToHex(hashState(state)), checkpoints };
}
